/* eslint-disable react/jsx-max-depth */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Formik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { Col, Row } from 'react-bootstrap';
import { PulseLoader } from 'react-spinners';
import { useDispatch } from 'react-redux';
import FormSection from '../../../components/containers/FormSection';
import Control from '../../../components/form/Control';
import Icon from '../../../components/icon/Icon';
import { IMAGE_CROPPER_MODAL } from '../../../components/modal/ImageCropper';
import { showModal } from '../../../redux/actions';
import {
  useGetCompanyProfileQuery,
  useUpdateCompanyProfileMutation,
} from '../../../redux/rtk';
import text from '../../../text';
import { checkFieldsAreDiff } from '../../../utilities/object';
import {
  companyFormSchema,
  SPECIALIZATION_MAX_LENGTH,
} from '../../../validation';
import AutoSave from './AutoSave';
import classNames from 'classnames';
import useUser from '../../../mp/hooks/useUser';

const CompanyForm = () => {
  const dispatch = useDispatch();
  const {
    isGovernment,
    hasValidMembership,
    isAdmin,
    isStaff,
    isUserUnderTrial,
  } = useUser();
  const { data: companyProfile, isLoading: isLoadingProfile } =
    useGetCompanyProfileQuery();
  const [updateCompanyProfile] = useUpdateCompanyProfileMutation();
  const imgRef = useRef(null);
  const [isImageLoading, setIsImageLoading] = useState(true);

  // Force reload the image when the logo_url changes
  useEffect(() => {
    if (!isEmpty(companyProfile?.logo_url) && imgRef.current) {
      const newImg = new Image();
      newImg.onload = () => {
        imgRef.current.src = companyProfile.logo_url;
      };
      newImg.src = companyProfile.logo_url;
    }
  }, [companyProfile?.logo_url]);

  const handleSubmit = (values, actions) => {
    const diffFields = checkFieldsAreDiff(companyProfile, values);
    if (Object.entries(diffFields).some(Boolean)) {
      const { logo_url, ...rest } = values;
      updateCompanyProfile(rest);
      actions.resetForm({ values, touched: diffFields });
    }
  };

  const handleEditLogoClicked = () => {
    dispatch(showModal(IMAGE_CROPPER_MODAL));
  };

  const isCompanyDetailsEditable = useMemo(
    () =>
      isGovernment ||
      (hasValidMembership && !isUserUnderTrial) ||
      isAdmin ||
      isStaff,
    [hasValidMembership, isGovernment, isUserUnderTrial, isAdmin, isStaff]
  );

  return (
    !isEmpty(companyProfile) &&
    !isLoadingProfile && (
      <FormSection title={text('companyDetails')} className='company-form'>
        <Formik
          validationSchema={companyFormSchema}
          initialValues={companyProfile}
          onSubmit={handleSubmit}
        >
          {({
            values: company,
            handleBlur,
            handleChange,
            touched,
            isValid,
            errors,
          }) => {
            const isFormTouched =
              touched && Object.values(touched).some(Boolean);
            return (
              <div>
                <Row>
                  <Col md='6'>
                    <Control
                      isSuccessful={touched.name}
                      isFailed={!!(isFormTouched && errors.name)}
                      as='input'
                      type='text'
                      name='name'
                      value={company.name ?? ''}
                      label={
                        <>
                          <span className='mr-1'>{text('companyName')}</span>
                          <span className='font-weight-normal'>
                            ({text('shownOnAd')})
                          </span>
                        </>
                      }
                      onBlur={handleBlur}
                      onChange={handleChange}
                      validationError={errors.name}
                      placeholder={text('companyNamePlaceholder')}
                      required={true}
                    />

                    {isCompanyDetailsEditable ? (
                      <>
                        <Control
                          isSuccessful={touched.specialization}
                          isFailed={!!(isFormTouched && errors.specialization)}
                          as='textarea'
                          id='specialization'
                          name='specialization'
                          value={company.specialization ?? ''}
                          label={
                            <>
                              <span className='mr-1'>
                                {text('companySpecialization')}
                              </span>
                              <span className='font-weight-normal'>
                                ({text('shownOnAd')})
                              </span>
                            </>
                          }
                          validationError={errors.specialization}
                          placeholder={text('companySpecializationPlaceholder')}
                          maxLength={SPECIALIZATION_MAX_LENGTH}
                          className='Specialization'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          isControlFeedbackHidden={true}
                          hint={`${(company.specialization ?? '').length}/${SPECIALIZATION_MAX_LENGTH} characters`}
                        />

                        <Control
                          isSuccessful={touched.address}
                          isFailed={!!(isFormTouched && errors.address)}
                          as='input'
                          type='text'
                          name='address'
                          value={company.address ?? ''}
                          label={text('companyAddress')}
                          validationError={errors.address}
                          placeholder={text('companyAddressPlaceholder')}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />

                        <Control
                          isSuccessful={!!(touched.phone && !errors.phone)}
                          isFailed={!!(isFormTouched && errors.phone)}
                          as='input'
                          type='text'
                          name='phone'
                          value={company.phone ?? ''}
                          label={text('companyPhone')}
                          validationError={errors.phone}
                          placeholder={text('companyPhonePlaceholder')}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                      </>
                    ) : null}
                  </Col>

                  {/* TODO: On mobile, upload logo should be first, followed by ads then everything else*/}

                  {isCompanyDetailsEditable ? (
                    <Col md='6'>
                      {/* SPACE FOR ADS HERE */}
                      <div className='CompanyLogo'>
                        <div className='label'>
                          <span className='font-weight-bold mr-1'>
                            {text('companyLogo')}
                          </span>
                          <span className='font-weight-normal'>
                            ({text('shownOnAd')})
                          </span>
                        </div>
                        <div className='d-flex flex-row align-items-center'>
                          <div className='position-relative'>
                            <img
                              ref={imgRef}
                              src={
                                !isEmpty(company.logo_url) && !isImageLoading
                                  ? company.logo_url
                                  : '/public/img/your_logo_placeholder.svg'
                              }
                              alt={
                                !isEmpty(company.logo_url)
                                  ? 'company logo'
                                  : 'your_logo_placeholder'
                              }
                              className={classNames(
                                'mr-3 company-logo-holder',
                                {
                                  objectFitContain: !isEmpty(company.logo_url),
                                  objectFitInitial: isEmpty(company.logo_url),
                                }
                              )}
                              loading='eager'
                              decoding='sync'
                              onLoad={() => setIsImageLoading(false)}
                              style={{ opacity: isImageLoading ? 0.3 : 1 }}
                            />
                            {isImageLoading ? (
                              <div className='position-absolute d-flex align-items-center justify-content-center image-loader-container'>
                                <PulseLoader size={8} color='#31a283' />
                              </div>
                            ) : null}
                          </div>
                          <Icon icon='edit' onClick={handleEditLogoClicked} />
                        </div>
                      </div>

                      <Control
                        isSuccessful={touched.website}
                        isFailed={!!(isFormTouched && errors.website)}
                        as='input'
                        type='text'
                        name='website'
                        value={company.website ?? ''}
                        label={text('companyWebsite')}
                        validationError={errors.website}
                        placeholder={text('companyWebsitePlaceholder')}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />

                      <Control
                        isSuccessful={touched.business_number}
                        isFailed={!!(isFormTouched && errors.business_number)}
                        as='input'
                        type='text'
                        name='business_number'
                        value={company.business_number ?? ''}
                        label={text('businessNumber')}
                        validationError={errors.business_number}
                        placeholder={text('companyBusinessNoPlaceholder')}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </Col>
                  ) : null}
                </Row>
                <AutoSave isValidForm={isValid} />
              </div>
            );
          }}
        </Formik>
      </FormSection>
    )
  );
};

export default CompanyForm;
